import { memo, useState } from 'react';
import { createPortal } from 'react-dom';
import { BookOpen, ChevronRight, Search, X } from 'lucide-react';
import { cleItem } from './selectionEvalNat';

/**
 * Le menu du QCM de suivi (11/10/2026), sur le modèle du panneau des attendus de QCMDashboard : les tests de
 * l'évaluation nationale de la classe, puis leurs items, avec une recherche par mots. Choisir un item ferme
 * le menu. Chaque item montre combien de questions en sont choisies (plus d'icône d'exerciseur : tous les
 * items en ont un, elle ne disait rien, retirée à la demande du prof le 11/10).
 * `formeParallele` (facultatif) : la compréhension de l'écrit sur d'autres textes, en tête, choisie d'un bloc
 * ({ cle, titre, sousTitre, compte }).
 */
const MenuItemsEvalNat = ({
  open, onClose, groupes, recherche, onRecherche, itemActif, onChoisir, comptes, sousTitre, totalChoisies,
  formeParallele = null, onChoisirForme,
}) => {
  const [testOuvert, setTestOuvert] = useState(null);
  const enRecherche = recherche.trim() !== '';

  return createPortal(
    <div
      className={`fixed inset-y-0 left-0 z-50 w-80 h-full overflow-hidden shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${
        open ? 'translate-x-0' : '-translate-x-full'
      }`}
      style={{ background: 'radial-gradient(ellipse at -50% 28%, #068aff, transparent 80%), #001a33' }}
      aria-hidden={!open}
    >
      <div className="px-[22px] pt-[22px] pb-[18px]" style={{ borderBottom: '1px solid rgba(148,163,184,0.08)' }}>
        <div className="flex items-center justify-between mb-[18px]">
          <div>
            <h2 className="text-[17px] font-bold tracking-tight text-slate-100">Évaluation nationale</h2>
            <p className="text-xs mt-[3px] text-[#b0bdd0]">{sousTitre}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer le menu des items"
            className="w-8 h-8 rounded-[10px] flex items-center justify-center transition hover:opacity-80"
            style={{ background: 'rgba(251,146,60,0.15)', border: '1px solid rgba(251,146,60,0.3)', color: '#fb923c' }}
          >
            <X size={16} />
          </button>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-[13px] top-1/2 -translate-y-1/2 text-[#b0bdd0]" />
          <input
            type="text"
            value={recherche}
            onChange={(e) => onRecherche(e.target.value)}
            placeholder="Rechercher un item, un mot de l'énoncé…"
            aria-label="Rechercher un item"
            className="w-full py-[11px] pl-[38px] pr-[14px] rounded-xl text-[13px] outline-none text-slate-200 bg-white/[0.07] border-[1.5px] border-slate-400/[0.12] focus:border-blue-500/30"
          />
        </div>
      </div>

      <div className="flex-1 overflow-auto py-2 panel-scrollbar">
        {formeParallele && !enRecherche ? (
          <button
            type="button"
            onClick={onChoisirForme}
            aria-current={itemActif === formeParallele.cle ? 'true' : undefined}
            className={`w-full flex items-start gap-2.5 text-left transition-all px-[18px] py-2.5 ${itemActif === formeParallele.cle ? 'bg-blue-500/[0.12]' : 'hover:bg-white/[0.08]'}`}
            style={{ borderLeft: `2.5px solid ${itemActif === formeParallele.cle ? '#3b82f6' : 'transparent'}` }}
          >
            <BookOpen size={15} className="shrink-0 mt-[2px] text-sky-300" />
            <span className="flex-1 min-w-0">
              <span className="block text-[13.5px] font-semibold text-slate-100">{formeParallele.titre}</span>
              <span className="block text-[11.5px] leading-relaxed text-[#8a9bb5]">{formeParallele.sousTitre}</span>
            </span>
            {formeParallele.compte > 0 ? (
              <span className="shrink-0 mt-[1px] text-[10px] font-semibold px-[6px] py-[2px] rounded-md text-blue-300 bg-blue-500/15 border border-blue-500/25">
                {formeParallele.compte}
              </span>
            ) : null}
          </button>
        ) : null}
        {groupes.length === 0 && !formeParallele ? <p className="text-sm text-center py-8 text-[#7d8da6]">Aucun item</p> : null}
        {groupes.map((groupe) => {
          const ouvert = enRecherche || testOuvert === groupe.typeLabel || (testOuvert === null && groupe.items.some((i) => cleItem(groupe.typeLabel, i.questionNum) === itemActif));
          return (
            <div key={groupe.typeLabel}>
              <button
                type="button"
                onClick={() => setTestOuvert(ouvert ? '' : groupe.typeLabel)}
                aria-expanded={ouvert}
                className={`w-full flex items-center gap-3 px-[18px] py-[10px] text-left transition-colors ${ouvert ? 'bg-sky-400/10' : ''}`}
                style={{ borderLeft: `2px solid ${ouvert ? '#38bdf8' : 'transparent'}` }}
              >
                <span className={`flex-1 min-w-0 truncate text-[13.5px] ${ouvert ? 'font-semibold text-slate-100' : 'font-medium text-slate-200'}`}>
                  {groupe.typeLabel}
                </span>
                <span className="shrink-0 text-[11px] font-semibold px-2 py-[2px] rounded-lg text-sky-300 bg-sky-400/10 border border-sky-400/20">
                  {groupe.items.length}
                </span>
                <ChevronRight size={12} className={`shrink-0 text-[#7d8da6] transition-transform duration-200 ${ouvert ? 'rotate-90' : ''}`} />
              </button>
              {ouvert ? groupe.items.map((item) => {
                const cle = cleItem(groupe.typeLabel, item.questionNum);
                const actif = cle === itemActif;
                const n = comptes.get(cle) || 0;
                const libelle = (item.officielle || item.variantes[0])?.nom_label || '';
                return (
                  <button
                    key={cle}
                    type="button"
                    onClick={() => onChoisir(groupe.typeLabel, item.questionNum)}
                    aria-current={actif ? 'true' : undefined}
                    className={`w-full flex items-start gap-2.5 text-left transition-all pl-[34px] pr-4 py-2 ${actif ? 'bg-blue-500/[0.12]' : 'hover:bg-white/[0.08]'}`}
                    style={{ borderLeft: `2.5px solid ${actif ? '#3b82f6' : 'transparent'}` }}
                  >
                    <span className="shrink-0 font-mono text-[11.5px] font-bold px-1.5 py-[1px] rounded-md mt-[1px] text-[#7cb3fc] bg-blue-400/15 border border-blue-400/35">
                      {item.questionNum}
                    </span>
                    <span className={`flex-1 text-[11.5px] leading-relaxed ${actif ? 'font-semibold text-slate-100' : 'text-[#8a9bb5]'}`}>
                      {libelle}
                    </span>
                    {n > 0 ? (
                      <span className="shrink-0 mt-[1px] text-[10px] font-semibold px-[6px] py-[2px] rounded-md text-blue-300 bg-blue-500/15 border border-blue-500/25">
                        {n}
                      </span>
                    ) : null}
                  </button>
                );
              }) : null}
            </div>
          );
        })}
      </div>

      <div className="px-[18px] py-[14px]" style={{ borderTop: '1px solid rgba(148,163,184,0.08)' }}>
        <p className="text-xs text-center text-[#b0bdd0]">
          {totalChoisies > 0 ? `${totalChoisies} question${totalChoisies > 1 ? 's' : ''} choisie${totalChoisies > 1 ? 's' : ''}` : 'Aucune question choisie'}
        </p>
      </div>
    </div>,
    document.body,
  );
};

export default memo(MenuItemsEvalNat);
